import React, { useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaChevronDown, FaRegCalendarAlt } from "react-icons/fa";

const selectClasses =
  "h-11 w-44 appearance-none rounded-xl border border-[#dbe3f5] bg-white py-2 pl-4 pr-10 text-[15px] font-medium text-[#24324d] shadow-none outline-none transition-all duration-200 hover:border-[#c8d3fb] hover:bg-[#f8faff] focus:border-[#aebcf1] focus:ring-4 focus:ring-[rgba(74,92,255,0.08)]";

const dateInputClasses =
  "h-11 w-44 rounded-xl border border-[#dbe3f5] bg-white pl-10 pr-3 text-center text-[14px] text-[#24324d] shadow-none outline-none transition-all duration-200 hover:border-[#c8d3fb] hover:bg-[#f8faff] focus:border-[#aebcf1] focus:ring-4 focus:ring-[rgba(74,92,255,0.08)]";

const labelClasses =
  "text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#7f90b3]";

const TimeRangeSelector = ({
  timeRange,
  setTimeRange,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  canShowAdvanced = false,
  allowToday = false,
}) => {
  const maxSelectableDate = new Date();
  if (!allowToday) {
    maxSelectableDate.setDate(maxSelectableDate.getDate() - 1);
  }

  useEffect(() => {
    if (!canShowAdvanced && ["90", "180", "365", "custom"].includes(timeRange)) {
      const newMaxSelectableDate = new Date();
      if (!allowToday) {
        newMaxSelectableDate.setDate(newMaxSelectableDate.getDate() - 1);
      }

      const newEndDate = new Date(newMaxSelectableDate);
      const newStartDate = new Date(newEndDate);
      newStartDate.setMonth(newEndDate.getMonth() - 1);
      newStartDate.setDate(newStartDate.getDate() + 1);
      setTimeRange("30");
      setStartDate(newStartDate);
      setEndDate(newEndDate);
    }
  }, [allowToday, canShowAdvanced, setEndDate, setStartDate, setTimeRange, timeRange]);

  const handleTimeRangeChange = (e) => {
    const range = e.target.value;
    setTimeRange(range);

    const newEndDate = new Date(maxSelectableDate);
    let newStartDate = new Date(newEndDate);

    const rangeMap = {
      "7": () => newStartDate.setDate(newEndDate.getDate() - 6),
      "14": () => newStartDate.setDate(newEndDate.getDate() - 13),
      "30": () => {
        newStartDate.setMonth(newEndDate.getMonth() - 1);
        newStartDate.setDate(newStartDate.getDate() + 1);
      },
      "90": () => {
        newStartDate.setMonth(newEndDate.getMonth() - 3);
        newStartDate.setDate(newStartDate.getDate() + 1);
      },
      "180": () => {
        newStartDate.setMonth(newEndDate.getMonth() - 6);
        newStartDate.setDate(newStartDate.getDate() + 1);
      },
      "365": () => {
        newStartDate.setFullYear(newEndDate.getFullYear() - 1);
        newStartDate.setDate(newStartDate.getDate() + 1);
      },
    };

    if (rangeMap[range]) {
      rangeMap[range]();
      setStartDate(newStartDate);
      setEndDate(newEndDate);
    }
  };

  const handleStartDateChange = (date) => {
    if (date > endDate) {
      setEndDate(date);
    }
    setStartDate(date);
    setTimeRange("custom");
  };

  const handleEndDateChange = (date) => {
    if (date < startDate) {
      setStartDate(date);
    }
    setEndDate(date);
    setTimeRange("custom");
  };

  return (
    <div>
      <label className="block">
        <div className={labelClasses}>Range</div>
        <div className="relative mt-2 inline-flex items-center">
          <select
            value={timeRange}
            onChange={handleTimeRangeChange}
            className={selectClasses}
          >
            <option value="7" className="text-sm">Past 7 Days</option>
            <option value="14" className="text-sm">Past 14 Days</option>
            <option value="30" className="text-sm">Past 30 Days</option>
            {canShowAdvanced && (
              <>
                <option value="90" className="text-sm">Past 3 Months</option>
                <option value="180" className="text-sm">Past 6 Months</option>
                <option value="365" className="text-sm">Past 12 Months</option>
                <option value="custom" className="text-sm">Customize</option>
              </>
            )}
          </select>
          <span className="pointer-events-none absolute right-3 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#eef2ff] text-[#4a63ff]">
            <FaChevronDown className="h-3 w-3" />
          </span>
        </div>
      </label>

      {timeRange === "custom" && canShowAdvanced && (
        <div className="mt-4 flex flex-wrap gap-4">
          <div className="relative">
            <label className={`mb-1 block ${labelClasses}`}>Start Date</label>
            <div className="relative">
                <DatePicker
                  selected={startDate}
                  onChange={handleStartDateChange}
                  maxDate={maxSelectableDate}
                  className={dateInputClasses}
                />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a97b4]">
                <FaRegCalendarAlt className="h-4 w-4" />
              </div>
            </div>
          </div>

          <div className="relative">
            <label className={`mb-1 block ${labelClasses}`}>End Date</label>
            <div className="relative">
                <DatePicker
                  selected={endDate}
                  onChange={handleEndDateChange}
                  minDate={startDate}
                  maxDate={maxSelectableDate}
                  className={dateInputClasses}
                />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a97b4]">
                <FaRegCalendarAlt className="h-4 w-4" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeRangeSelector;
